/**
 * Iter 145bs — Week's Best (Mon→Sun) panel.
 *
 * Shows the top-N picks per UTC day (default 5) for the current week,
 * grouped by weekday.  Designed to sit alongside the Daily-25 slate as
 * an alternate "focus mode" view: one screen, every pick that matters,
 * already settled where possible.
 *
 * Toggle controlled by the parent — this component focuses purely on
 * rendering once `view === "week-best"`.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import {
  CalendarRange,
  CheckCircle2, XCircle, MinusCircle, Clock as ClockIcon,
  TrendingUp, Loader2, ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import MatchAnalysisModal from "./MatchAnalysisModal";
import { appTodayIso, addDaysIso } from "../lib/appTime";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const fmtKickoff = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

const OutcomePill = ({ outcome }) => {
  if (!outcome || outcome === "pending") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border text-amber-300 border-amber-500/30 bg-amber-500/5">
        <ClockIcon className="w-2.5 h-2.5" /> Pending
      </span>
    );
  }
  const cfg = outcome === "hit"
    ? { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Hit" }
    : outcome === "miss"
    ? { Icon: XCircle, cls: "text-rose-300 border-rose-500/40 bg-rose-500/10", label: "Miss" }
    : { Icon: MinusCircle, cls: "text-slate-300 border-slate-500/40 bg-slate-500/10", label: "Void" };
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{label}
    </span>
  );
};

const PickCard = ({ p, onAnalyze, analysisLoadingFid, marketLabel = "HALFTIME", accentColor = "text-amber-300", accentColorMuted = "text-amber-300/80", accentBorder = "border-amber-500/30", accentBg = "amber-500" }) => {
  const pick = p.pick || {};
  const home = p.home_team || {};
  const away = p.away_team || {};
  const league = p.league || {};
  const isLoading = analysisLoadingFid === p.fixture_id;
  return (
    <article
      className="rounded-xl border border-border bg-card/50 p-3.5 hover:bg-card/70 hover:border-foreground/30 transition-colors"
      data-testid={`week-best-pick-${p.fixture_id}`}
    >
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <span className="truncate flex-1">{league.name || "—"}</span>
        <span className="inline-flex items-center gap-1 shrink-0">
          <ClockIcon className="w-3 h-3" />
          {fmtKickoff(p.kickoff)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
        <span className="font-semibold truncate flex-1 text-sm">{home.name || "Home"}</span>
        <span className="text-[10px] uppercase text-muted-foreground tracking-wider shrink-0">vs</span>
        <span className="font-semibold truncate flex-1 text-right text-sm">{away.name || "Away"}</span>
      </div>
      <div className="rounded-lg border border-border/50 bg-background/40 px-3 py-2 mb-2.5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className={`text-[9px] uppercase tracking-[0.18em] font-bold ${accentColorMuted} truncate`}>
            {pick.market_type || marketLabel}
          </span>
          <OutcomePill outcome={p.outcome} />
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-display font-bold truncate text-base">{pick.prediction || "—"}</span>
          <span className="font-bold tabular-nums text-emerald-300 text-base shrink-0">
            {pick.confidence != null ? `${Math.round(pick.confidence)}%` : "—"}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onAnalyze(p.fixture_id)}
        disabled={isLoading}
        data-testid={`week-best-pick-${p.fixture_id}-analyze`}
        className={`w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold ${accentColor} hover:opacity-80 py-1.5 rounded-md border ${accentBorder} hover:bg-card/60 transition-colors disabled:opacity-60`}
      >
        {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
        Deep analysis
      </button>
    </article>
  );
};

const DayBlock = ({ day, onAnalyze, analysisLoadingFid, isToday, cfg }) => {
  const s = day.summary || {};
  return (
    <section
      className="rounded-2xl border border-border bg-background/30 p-4 sm:p-5"
      data-testid={`week-best-day-${day.date}`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 mb-4 pb-3 border-b border-border/40">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {day.weekday}
            {isToday && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300/80 align-middle">
                · Today
              </span>
            )}
          </h3>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {fmtDate(day.date)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-bold">
          {s.graded > 0 && (
            <span className="text-emerald-300/90">
              {s.hit}H · {s.miss}M
              {s.hit_rate != null && <span className="ml-1 text-emerald-200">· {s.hit_rate}%</span>}
            </span>
          )}
          {s.pending > 0 && (
            <span className="text-amber-300/80">· {s.pending} pending</span>
          )}
        </div>
      </header>
      {day.picks && day.picks.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {day.picks.map((p) => (
            <PickCard
              key={p.fixture_id}
              p={p}
              onAnalyze={onAnalyze}
              analysisLoadingFid={analysisLoadingFid}
              marketLabel={cfg?.marketLabel}
              accentColor={cfg?.accentColor}
              accentColorMuted={cfg?.accentColorMuted}
              accentBorder={cfg?.accentBorder}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border/50 bg-card/20 px-4 py-6 text-center text-xs text-muted-foreground">
          No picks compiled for {day.weekday} yet.
        </div>
      )}
    </section>
  );
};

export default function WeekBestPanel({
  market = "halftime",
}) {
  const cfg = market === "double-chance"
    ? {
        endpoint: "/double-chance/week-best",
        label: "Double Chance",
        marketLabel: "DOUBLE CHANCE",
        accentColor: "text-emerald-300",
        accentColorMuted: "text-emerald-300/80",
        accentBorder: "border-emerald-500/30",
        accentBg: "from-emerald-500/10",
        ringBorder: "border-emerald-500/20",
        emptyLabel: "Week's Best",
      }
    : {
        endpoint: "/halftime-betslip/week-best",
        label: "Halftime",
        marketLabel: "HALFTIME",
        accentColor: "text-amber-300",
        accentColorMuted: "text-amber-300/90",
        accentBorder: "border-amber-500/30",
        accentBg: "from-amber-500/10",
        ringBorder: "border-amber-500/20",
        emptyLabel: "Week's Best",
      };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(null);  // null = current week
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoadingFid, setAnalysisLoadingFid] = useState(null);

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const params = {};
      if (ws) params.week_start = ws;
      const r = await axios.get(`${API}${cfg.endpoint}`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(`Failed to load ${cfg.label} Week's Best slate`);
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint, cfg.label]);

  useEffect(() => { load(weekStart); }, [load, weekStart]);

  const shiftWeek = (deltaDays) => {
    if (!data?.week_start) return;
    setWeekStart(addDaysIso(data.week_start, deltaDays));
  };

  const openAnalysis = useCallback(async (fid) => {
    if (!fid) return;
    setAnalysisLoadingFid(fid);
    try {
      const { data: a } = await axios.get(
        `${API}/predictions/${fid}/analysis`,
        { timeout: 30000 }
      );
      setAnalysisData(a);
      setAnalysisOpen(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Analysis not available yet for this fixture.");
      } else {
        toast.error("Failed to load match analysis");
      }
    } finally {
      setAnalysisLoadingFid(null);
    }
  }, []);

  const todayIso = appTodayIso();
  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="week-best-panel">
      {/* Week header strip */}
      <div className={`rounded-2xl border ${cfg.accentBorder} bg-gradient-to-br ${cfg.accentBg} via-background to-background p-5 sm:p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold ${cfg.accentColorMuted}`}>
              <Sparkles className="w-3 h-3" /> Focus mode · {cfg.label} Week's Best
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1.5">
              {data?.top_per_day || 5} highest-conviction picks · every day · Mon → Sun
            </h2>
            {data && (
              <p className="text-xs text-muted-foreground mt-1">
                {fmtDate(data.week_start)} → {fmtDate(data.week_end)}
                {s.n_picks > 0 && (
                  <> · {s.n_picks} picks compiled</>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => shiftWeek(-7)}
              data-testid="week-best-prev"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:bg-card transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setWeekStart(null)}
              data-testid="week-best-this-week"
              className="px-3 h-9 rounded-full border border-border text-[10px] uppercase tracking-[0.14em] font-bold hover:bg-card transition-colors"
            >
              This week
            </button>
            <button
              type="button"
              onClick={() => shiftWeek(7)}
              data-testid="week-best-next"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:bg-card transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Week-level summary stats */}
        {data && s.n_picks > 0 && (
          <dl
            className={`grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 pt-5 border-t ${cfg.ringBorder}`}
            data-testid="week-best-summary"
          >
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Total picks</dt>
              <dd className="font-display text-2xl font-bold tabular-nums">{s.n_picks}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Hits</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-emerald-300">{s.hit || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Misses</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-rose-300">{s.miss || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Pending</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-amber-300/90">{s.pending || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Hit rate</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-emerald-200">
                {s.hit_rate != null ? `${s.hit_rate}%` : "—"}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* Day-by-day grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 rounded-2xl border border-border bg-card/30 animate-pulse" />
          ))}
        </div>
      ) : !data || s.n_picks === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/20 p-10 text-center">
          <CalendarRange className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No picks have been compiled for this week yet.
            {weekStart && " Try going back to this week."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {(data.days || []).map((day) => (
            <DayBlock
              key={day.date}
              day={day}
              isToday={day.date === todayIso}
              onAnalyze={openAnalysis}
              analysisLoadingFid={analysisLoadingFid}
              cfg={cfg}
            />
          ))}
        </div>
      )}

      {analysisOpen && analysisData && (
        <MatchAnalysisModal
          analysis={analysisData}
          onClose={() => { setAnalysisOpen(false); setAnalysisData(null); }}
        />
      )}
    </div>
  );
}
